import React, {
  useState,
  useEffect,
  useMemo,
  useRef,
  useCallback,
} from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import PageHeader from "../components/PageHeader";
import StatChip from "../components/StatChip";
import TargetDateChips from "../components/TargetDateChips";
import useTargetDateFilter from "../hooks/useTargetDateFilter";
// --- Components ---
import ReportTable from "../components/ReportTable";
import Modal from "../components/Modal";
import Loader from "../components/Loader";
import "../assets/css/modal.css";
import "../assets/css/reportTable.css";

// Task Selectors and Actions
import {
  selectGetTaskAssignmentStatus,
  selectGetTaskAssignment,
} from "../redux/tasks/taskSelectors";
import {
  getTaskAssignmentApis,
  tasksAssignApis,
} from "../redux/tasks/taskActions";

// User Selectors and Actions
import { selectUsers } from "../redux/users/userSelectors";
import { usersApis } from "../redux/users/userActions";
import {
  formatDateOnly,
  getAvatarColor,
  getCurrentRole,
  minutesToHHMM,
  targetDateState,
  timeToMinutes,
  toDateOnly,
} from "../utils/common";
import SkDatePicker from "../components/SkDatePicker";
import PortalDropdown from "../components/PortalDropdown";
import TaskImages from "../components/TaskImages";
import {
  getPriorityColors,
  getAssignmentColors,
  getTeamColors,
} from "../utils/badgeColors";

// Team Selectors and Actions
import TaskTimeline from "../components/TaskTimeline";
import {
  teamListApis,
  teamMembersApis,
  taskTimelineApis,
} from "../redux/team/teamActions";
import {
  selectTaskTimeline,
  selectTaskTimelineStatus,
} from "../redux/team/teamSelectors";
import { selectTeams } from "../redux/team/teamSelectors";
import { useTabRefresh } from "../context/TabsContext";

// ==========================================
// SUB-COMPONENTS
// ==========================================

const PriorityFilterDropdown = ({ value, onChange }) => {
  const [isOpen, setIsOpen] = useState(false);
  const wrapperRef = useRef(null);
  const currentStyle = getAssignmentColors(value);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target))
        setIsOpen(false);
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  return (
    <div
      ref={wrapperRef}
      className="position-relative d-inline-block w-100 w-md-auto"
    >
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="d-flex align-items-center justify-content-between justify-content-md-start gap-2 shadow-sm w-100 w-md-auto"
        style={{
          backgroundColor: currentStyle.bg,
          color: currentStyle.text,
          border: currentStyle.border,
          padding: "8px 12px",
          borderRadius: "8px",
          fontSize: "0.85rem",
          fontWeight: "600",
          cursor: "pointer",
        }}
      >
        <div className="d-flex align-items-center gap-2">
          <i className="bi bi-funnel-fill"></i>
          <span className="text-capitalize">{value}</span>
        </div>
        <i className="bi bi-chevron-down opacity-75"></i>
      </button>
      {isOpen && (
        <div
          className="shadow-lg p-1"
          style={{
            position: "absolute",
            top: "115%",
            right: 0,
            minWidth: "140px",
            width: "100%",
            backgroundColor: "#fff",
            borderRadius: "8px",
            zIndex: 1000,
            border: "1px solid rgba(0,0,0,0.08)",
          }}
        >
          {["assigned", "unassigned"].map((opt) => (
            <div
              key={opt}
              onClick={() => {
                onChange(opt);
                setIsOpen(false);
              }}
              className="d-flex align-items-center gap-2 px-3 py-2 rounded-1 text-capitalize"
              style={{
                cursor: "pointer",
                fontSize: "0.85rem",
                fontWeight: value === opt ? "600" : "500",
                backgroundColor: value === opt ? "#f8f9fa" : "transparent",
              }}
            >
              <i
                className="bi bi-circle-fill"
                style={{
                  fontSize: "0.5rem",
                  color: opt === "assigned" ? "#f57c00" : "#d32f2f",
                }}
              ></i>
              {opt}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// --- PrioritySelect using Portal ---
const PrioritySelect = ({ initialValue, onChange, readOnly = false }) => {
  const [value, setValue] = useState(initialValue || "Medium");
  const [isOpen, setIsOpen] = useState(false);
  const buttonRef = useRef(null);

  useEffect(() => setValue(initialValue), [initialValue]);

  const getColorMap = getPriorityColors;
  const colors = getColorMap(value);

  const handleToggle = (e) => {
    e.preventDefault();
    e.stopPropagation(); // Stop click from propagating
    if (readOnly) return;

    setIsOpen(!isOpen);
  };

  return (
    <>
      <button
        ref={buttonRef}
        type="button"
        onClick={handleToggle}
        style={{
          backgroundColor: colors.bg,
          color: colors.text,
          border: "none",
          fontSize: "0.75rem",
          fontWeight: "600",
          padding: "6px 12px",
          borderRadius: "12px",
          display: "inline-flex",
          alignItems: "center",
          gap: "8px",
          cursor: readOnly ? "default" : "pointer",
        }}
      >
        {value}{" "}
        {!readOnly && (
          <i className={`bi bi-chevron-${isOpen ? "up" : "down"}`}></i>
        )}
      </button>

      <PortalDropdown
        isOpen={isOpen}
        onClose={() => setIsOpen(false)}
        anchorRef={buttonRef}
      >
        <div className="p-2">
          {["High", "Medium", "Low"].map((option) => (
            <div
              key={option}
              onClick={(e) => {
                e.stopPropagation();
                setValue(option); // Optimistic UI update
                onChange(option);
                setIsOpen(false);
              }}
              className="d-flex align-items-center gap-2 rounded-2 px-2 py-2 mb-1 dropdown-item-hover"
              style={{
                cursor: "pointer",
                fontSize: "0.85rem",
                backgroundColor: value === option ? "#f8f9fa" : "transparent",
              }}
              onMouseEnter={(e) =>
                (e.currentTarget.style.backgroundColor = "#f8f9fa")
              }
              onMouseLeave={(e) =>
              (e.currentTarget.style.backgroundColor =
                value === option ? "#f8f9fa" : "transparent")
              }
            >
              <i
                className={`bi bi-circle-fill text-${getColorMap(option).variant
                  }`}
                style={{ fontSize: "0.5rem" }}
              ></i>
              <span style={{ color: getColorMap(option).text }}>{option}</span>
            </div>
          ))}
        </div>
      </PortalDropdown>
    </>
  );
};

/* --- TeamSelect using Portal ---
   Corrects the team chosen when the task was raised. This is the only place
   the team can be changed without following the Database -> Web App -> Testing
   routes, because it is fixing a mistake rather than moving work along — the
   server treats it the same way and records it as a correction.

   Changing the team clears the assignee: whoever held it is in the old team,
   and the server would refuse to leave them on it. */
const TeamSelect = ({ initialValue, teams = [], onChange, readOnly = false }) => {
  const [isOpen, setIsOpen] = useState(false);
  const buttonRef = useRef(null);

  const current = teams.find((t) => t.TeamCode === initialValue);
  const colors = getTeamColors(current?.TeamName);

  const handleToggle = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (readOnly) return;

    setIsOpen(!isOpen);
  };

  return (
    <>
      <button
        ref={buttonRef}
        type="button"
        onClick={handleToggle}
        title={current ? `Held by ${current.TeamName}` : "No team set"}
        style={{
          backgroundColor: colors.bg,
          color: colors.text,
          border: current ? "none" : "1px dashed #ced4da",
          fontSize: "0.72rem",
          fontWeight: 600,
          padding: "6px 10px",
          borderRadius: "12px",
          display: "inline-flex",
          alignItems: "center",
          gap: "6px",
          cursor: readOnly ? "default" : "pointer",
          whiteSpace: "nowrap",
        }}
      >
        {current?.TeamName || "No team"}
        {!readOnly && (
          <i className={`bi bi-chevron-${isOpen ? "up" : "down"}`}></i>
        )}
      </button>

      <PortalDropdown
        isOpen={isOpen}
        onClose={() => setIsOpen(false)}
        anchorRef={buttonRef}
      >
        <div className="p-2">
          <div className="text-muted small fw-bold px-2 pb-2">Move to team</div>
          {teams.map((t) => (
            <div
              key={t.TeamCode}
              onClick={(e) => {
                e.stopPropagation();
                setIsOpen(false);
                if (t.TeamCode !== initialValue) onChange(t.TeamCode);
              }}
              className="d-flex align-items-center gap-2 rounded-2 px-2 py-2 mb-1"
              style={{
                cursor: "pointer",
                fontSize: "0.85rem",
                backgroundColor:
                  t.TeamCode === initialValue ? "#f8f9fa" : "transparent",
              }}
              onMouseEnter={(e) =>
                (e.currentTarget.style.backgroundColor = "#f8f9fa")
              }
              onMouseLeave={(e) =>
              (e.currentTarget.style.backgroundColor =
                t.TeamCode === initialValue ? "#f8f9fa" : "transparent")
              }
            >
              <i
                className="bi bi-circle-fill"
                style={{
                  fontSize: "0.5rem",
                  color: getTeamColors(t.TeamName).text,
                }}
              ></i>
              <span style={{ color: getTeamColors(t.TeamName).text }}>
                {t.TeamName}
              </span>
              {t.TeamCode === initialValue && (
                <i className="bi bi-check2 ms-auto text-muted"></i>
              )}
            </div>
          ))}
        </div>
      </PortalDropdown>
    </>
  );
};

// --- QueryTypeSelect using Portal ---
const QueryTypeSelect = ({ initialValue, onChange, readOnly = false }) => {
  const [value, setValue] = useState(initialValue || "New Task");
  const [isOpen, setIsOpen] = useState(false);
  const buttonRef = useRef(null);

  useEffect(() => setValue(initialValue), [initialValue]);

  const getColorMap = (val) => {
    const v = String(val || "").toLowerCase();
    if (v === "bug") return { bg: "#ffe5e5", text: "#d32f2f" };
    if (v === "rework") return { bg: "#fff8e1", text: "#f57c00" };
    return { bg: "#e3f2fd", text: "#0d47a1" };
  };
  const colors = getColorMap(value);

  const handleToggle = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (readOnly) return;

    setIsOpen(!isOpen);
  };

  return (
    <>
      <button
        ref={buttonRef}
        type="button"
        onClick={handleToggle}
        style={{
          backgroundColor: colors.bg,
          color: colors.text,
          border: "none",
          fontSize: "0.75rem",
          fontWeight: "600",
          padding: "6px 12px",
          borderRadius: "12px",
          display: "inline-flex",
          alignItems: "center",
          gap: "8px",
          cursor: readOnly ? "default" : "pointer",
          whiteSpace: "nowrap",
        }}
      >
        {value}{" "}
        {!readOnly && (
          <i className={`bi bi-chevron-${isOpen ? "up" : "down"}`}></i>
        )}
      </button>

      <PortalDropdown
        isOpen={isOpen}
        onClose={() => setIsOpen(false)}
        anchorRef={buttonRef}
      >
        <div className="p-2">
          {["Bug", "New Task", "Rework"].map((option) => (
            <div
              key={option}
              onClick={(e) => {
                e.stopPropagation();
                setValue(option); // Optimistic UI update
                onChange(option);
                setIsOpen(false);
              }}
              className="d-flex align-items-center gap-2 rounded-2 px-2 py-2 mb-1 dropdown-item-hover"
              style={{
                cursor: "pointer",
                fontSize: "0.85rem",
                backgroundColor: value === option ? "#f8f9fa" : "transparent",
              }}
              onMouseEnter={(e) =>
                (e.currentTarget.style.backgroundColor = "#f8f9fa")
              }
              onMouseLeave={(e) =>
              (e.currentTarget.style.backgroundColor =
                value === option ? "#f8f9fa" : "transparent")
              }
            >
              <i
                className="bi bi-circle-fill"
                style={{ fontSize: "0.5rem", color: getColorMap(option).text }}
              ></i>
              <span className="text-dark">{option}</span>
            </div>
          ))}
        </div>
      </PortalDropdown>
    </>
  );
};

// --- IsUpdateSelect using Portal ---
/* `needed` = this row cannot be assigned until this cell is filled.
 *
 * The three prerequisites — Is Update, Duration, Target Date — were only
 * ever mentioned at the moment somebody tried to assign, which is the one
 * moment they are inconvenient. Marked on the row instead, they can be
 * filled while reading it, and the assign click then succeeds first time.
 *
 * Only on rows waiting to be assigned: an already-assigned task cleared
 * these on its way through, and outlining them again would be a warning
 * about nothing. */
const IsUpdateSelect = ({
  initialValue,
  onChange,
  readOnly = false,
  needed = false,
}) => {
  // Sets "None" initially if no value is present
  const [value, setValue] = useState(initialValue || "None");
  const [isOpen, setIsOpen] = useState(false);
  const buttonRef = useRef(null);

  useEffect(() => setValue(initialValue || "None"), [initialValue]);

  const getColorMap = (val) => {
    const v = String(val || "").toLowerCase();
    if (v === "backend work") return { bg: "#f3e5f5", text: "#7b1fa2" }; // Purple
    if (v === "update") return { bg: "#e8f5e9", text: "#2e7d32" }; // Green
    if (v === "not update") return { bg: "#fff3e0", text: "#e65100" }; // Orange
    return { bg: "#f8f9fa", text: "#6c757d" }; // Gray for None
  };
  const colors = getColorMap(value);

  const handleToggle = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (readOnly) return;

    setIsOpen(!isOpen);
  };

  return (
    <>
      <button
        ref={buttonRef}
        type="button"
        onClick={handleToggle}
        title={
          needed ? "Set this before the task can be assigned" : undefined
        }
        style={{
          backgroundColor: colors.bg,
          color: colors.text,
          /* The same red edge the empty Target Date cell uses, so "this row
             still needs something" reads the same wherever it appears. */
          border: needed ? "1px solid #ef4444" : "none",
          fontSize: "0.75rem",
          fontWeight: "600",
          padding: "6px 12px",
          borderRadius: "12px",
          display: "inline-flex",
          alignItems: "center",
          gap: "8px",
          cursor: readOnly ? "default" : "pointer",
          whiteSpace: "nowrap",
        }}
      >
        {value}{" "}
        {!readOnly && (
          <i className={`bi bi-chevron-${isOpen ? "up" : "down"}`}></i>
        )}
      </button>

      <PortalDropdown
        isOpen={isOpen}
        onClose={() => setIsOpen(false)}
        anchorRef={buttonRef}
      >
        <div className="p-2">
          {/* "None" is deliberately missing from the list. They MUST choose one. */}
          {["Backend work", "Update", "Not update"].map((option) => (
            <div
              key={option}
              onClick={(e) => {
                e.stopPropagation();
                // 1. Instantly update the UI state locally
                setValue(option);
                // 2. Trigger the API call
                onChange(option);
                setIsOpen(false);
              }}
              className="d-flex align-items-center gap-2 rounded-2 px-2 py-2 mb-1 dropdown-item-hover"
              style={{
                cursor: "pointer",
                fontSize: "0.85rem",
                backgroundColor: value === option ? "#f8f9fa" : "transparent",
              }}
              onMouseEnter={(e) =>
                (e.currentTarget.style.backgroundColor = "#f8f9fa")
              }
              onMouseLeave={(e) =>
              (e.currentTarget.style.backgroundColor =
                value === option ? "#f8f9fa" : "transparent")
              }
            >
              <i
                className="bi bi-circle-fill"
                style={{ fontSize: "0.5rem", color: getColorMap(option).text }}
              ></i>
              <span className="text-dark">{option}</span>
            </div>
          ))}
        </div>
      </PortalDropdown>
    </>
  );
};

// --- DurationEditor ---
const DurationEditor = ({
  initialDuration,
  onSave,
  readOnly = false,
  /* See IsUpdateSelect — same flag, same reason. */
  needed = false,
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [val, setVal] = useState(initialDuration || "00 : 00");
  const inputRef = useRef(null);

  useEffect(() => setVal(initialDuration || "00 : 00"), [initialDuration]);
  useEffect(() => {
    if (isEditing && inputRef.current) inputRef.current.select();
  }, [isEditing]);

  const handleBlur = () => {
    setIsEditing(false);
    if (
      val.replace(/\s/g, "") !==
      (initialDuration || "00 : 00").replace(/\s/g, "")
    )
      onSave(val);
    else setVal(initialDuration || "00 : 00");
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") inputRef.current.blur();
    if (e.key === "Escape") {
      setVal(initialDuration || "00 : 00");
      setIsEditing(false);
    }
  };

  if (isEditing) {
    return (
      <div style={{ width: "100px", margin: "0 auto" }}>
        <input
          ref={inputRef}
          type="text"
          value={val}
          onChange={(e) => setVal(e.target.value)}
          onBlur={handleBlur}
          onKeyDown={handleKeyDown}
          className="form-control form-control-sm text-center p-1"
          style={{
            fontWeight: "600",
            fontSize: "0.9rem",
            letterSpacing: "1px",
          }}
        />
      </div>
    );
  }
  return (
    <div
      onClick={() => !readOnly && setIsEditing(true)}
      className="text-center"
      title={
        needed ? "Set this before the task can be assigned" : undefined
      }
      style={{
        cursor: readOnly ? "default" : "pointer",
        padding: needed ? "5px" : "6px",
        borderRadius: "6px",
        border: needed ? "1px solid #ef4444" : "1px solid transparent",
      }}
    >
      <span className={`fw-medium${needed ? " text-danger" : ""}`}>{val}</span>{" "}
      {!readOnly && (
        <i
          className="bi bi-pencil-fill ms-2 text-muted opacity-25"
          style={{ fontSize: "0.7rem" }}
        ></i>
      )}
    </div>
  );
};

/* --- TargetDateEditor ---

   Shown open, not behind a click like DurationEditor. The two are not the
   same job: a duration is nearly always already there and is occasionally
   corrected, while a target date is the field this screen refuses to assign
   without — so an empty one has to look unanswered rather than look like a
   value of "-". A red-edged empty box says that without a word of copy.

   The calendar renders through a portal, so the grid's own overflow cannot
   clip it. */
const TargetDateEditor = ({
  value,
  onSave,
  readOnly = false,
  /* Red only where the emptiness is actually in the way — a row waiting to be
     assigned. Tasks handed out before this field existed have no date and
     never needed one; painting a hundred of those red would say "something is
     wrong here" about rows where nothing is. */
  required = false,
}) => (
  <div style={{ minWidth: "128px" }}>
    <SkDatePicker
      size="sm"
      block
      value={value}
      /* Only a real change is saved. react-datepicker fires onChange when the
         same day is clicked again, and every save on this grid is a round trip
         plus a reload of the list. */
      onChange={(iso) => {
        if (iso && iso !== value) onSave(iso);
      }}
      disabled={readOnly}
      invalid={required && !value}
      placeholder="Set date"
      ariaLabel="Target date"
    />
  </div>
);

// --- UserSelect with Portal ---
const UserSelect = ({ initialUser, onAssign, userOptions = [], readOnly = false }) => {
  const [selectedUser, setSelectedUser] = useState(initialUser);
  const [isOpen, setIsOpen] = useState(false);
  const [menuStyle, setMenuStyle] = useState({});
  const wrapperRef = useRef(null);
  const menuRef = useRef(null)

  useEffect(() => {
    setSelectedUser(initialUser);
  }, [initialUser]);

  useEffect(() => {
    // 1. Handle Clicks Outside
    const handleClickOutside = (event) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    // 2. Handle Scrolling
    const handleScroll = (event) => {
      // If the dropdown is open...
      if (isOpen) {
        // ...and the element being scrolled is INSIDE our dropdown (e.g., the user list),
        // then DO NOT close the menu.
        if (wrapperRef.current && wrapperRef.current.contains(event.target)) {
          return;
        }
        // Otherwise (page scroll), close it.
        setIsOpen(false);
      }
    };

    // Attach listeners
    document.addEventListener("mousedown", handleClickOutside);
    // 'true' uses capture mode to catch all scroll events
    window.addEventListener("scroll", handleScroll, true);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      window.removeEventListener("scroll", handleScroll, true);
    };
  }, [isOpen]);

  const toggleMenu = (e) => {
    e.stopPropagation();
    if (readOnly) return;

    if (isOpen) {
      setIsOpen(false);
      return;
    }

    if (wrapperRef.current) {
      const rect = wrapperRef.current.getBoundingClientRect();

      const viewportHeight = window.innerHeight;
      const viewportWidth = window.innerWidth;

      const menuWidth = 220;
      const menuHeightEstimate = 250;

      const spaceBelow = viewportHeight - rect.bottom;
      const spaceAbove = rect.top;

      // Align dropdown's RIGHT edge with the Assign icon
      const rightPos = viewportWidth - rect.right;

      let newStyle = {
        position: "fixed",
        width: `${menuWidth}px`,
        right: `${Math.max(10, rightPos)}px`,
        zIndex: 9999,
        backgroundColor: "#fff",
        borderRadius: "10px",
        border: "1px solid rgba(0,0,0,0.08)",
        boxShadow: "0 10px 25px rgba(0,0,0,0.15)",
      };

      // Open downward
      if (spaceBelow >= menuHeightEstimate || spaceBelow >= spaceAbove) {
        newStyle.top = `${rect.bottom + 5}px`;
        newStyle.bottom = "auto";
      }

      // Open upward
      else {
        newStyle.bottom = `${viewportHeight - rect.top + 5}px`;
        newStyle.top = "auto";
      }

      setMenuStyle(newStyle);
      setIsOpen(true);
    }
  };

  const getInitials = (name) => {
    if (!name) return "";
    const p = name.trim().split(" ");
    return p.length === 1
      ? p[0].charAt(0).toUpperCase()
      : (p[0].charAt(0) + p[p.length - 1].charAt(0)).toUpperCase();
  };

  return (
    <div
      ref={wrapperRef}
      className="d-inline-block"
      onClick={(e) => e.stopPropagation()}
    >
      <div onClick={toggleMenu} style={{ cursor: readOnly ? "default" : "pointer" }}>
        {selectedUser ? (
          <div
            style={{
              width: "35px",
              height: "35px",
              borderRadius: "50%",
              backgroundColor: getAvatarColor(selectedUser),
              color: "#fff",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontWeight: "600",
              fontSize: "0.85rem",
            }}
          >
            {getInitials(selectedUser)}
          </div>
        ) : (
          <div
            style={{
              width: "35px",
              height: "35px",
              borderRadius: "50%",
              backgroundColor: "#e9ecef",
              border: "1px solid #dee2e6",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: "#6c757d",
            }}
          >
            <i className="bi bi-person-plus-fill"></i>
          </div>
        )}
      </div>

      {isOpen && (
        <div style={menuStyle}>
          <div className="text-muted small border-bottom mb-1 fw-bold p-2 bg-light rounded-top">
            Select User
          </div>
          {/* <div style={{ maxHeight: "200px", overflowY: "auto" }}>
            {userOptions.map((u) => (
              <div
                key={u.UserCode}
                onClick={() => {
                  onAssign(u);
                  setIsOpen(false);
                }}
                className="d-flex align-items-center gap-2 p-2 mx-1 rounded-2"
                style={{
                  cursor: "pointer",
                  backgroundColor:
                    selectedUser === u.UserName ? "#f0f8ff" : "transparent",
                }}
              >
                <div
                  style={{
                    width: "30px",
                    height: "30px",
                    borderRadius: "50%",
                    backgroundColor: getAvatarColor(u.UserName),
                    color: "#fff",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: "0.75rem",
                  }}
                >
                  {getInitials(u.UserName)}
                </div>
                <div className="d-flex flex-column text-start overflow-hidden">
                  <span className="small fw-bold text-dark">{u.UserName}</span>
                  <span
                    className="extra-small text-muted"
                    style={{ fontSize: "11px" }}
                  >
                    {u.EMail}
                  </span>
                </div>
              </div>
            ))}
          </div> */}
          <div style={{ maxHeight: "200px", overflowY: "auto" }}>
            {userOptions.length > 0 ? (
              userOptions.map((u) => (
                <div
                  key={u.UserCode}
                  onClick={() => {
                    onAssign(u);
                    setIsOpen(false);
                  }}
                  className="d-flex align-items-center gap-2 p-2 mx-1 rounded-2"
                  style={{
                    cursor: "pointer",
                    backgroundColor:
                      selectedUser === u.UserName ? "#f0f8ff" : "transparent",
                  }}
                >
                  <div
                    style={{
                      width: "30px",
                      height: "30px",
                      minWidth: "30px",
                      borderRadius: "50%",
                      backgroundColor: getAvatarColor(u.UserName),
                      color: "#fff",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontSize: "0.75rem",
                    }}
                  >
                    {getInitials(u.UserName)}
                  </div>

                  <div className="d-flex flex-column text-start overflow-hidden">
                    <span className="small fw-bold text-dark">
                      {u.UserName}
                    </span>

                    <span
                      className="text-muted"
                      style={{
                        fontSize: "11px",
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                      }}
                    >
                      {u.EMail || "-"}
                    </span>
                  </div>
                </div>
              ))
            ) : (
              <div
                className="text-center text-muted py-4"
                style={{ fontSize: "0.8rem" }}
              >
                <i className="bi bi-people mb-2 d-block"></i>
                No users found
              </div>
            )}
          </div>
          <div
            onClick={() => {
              onAssign(null);
              setIsOpen(false);
            }}
            className="p-2 text-danger small border-top mt-1 hover-bg-light rounded-bottom text-center"
            style={{ cursor: "pointer" }}
          >
            <i className="bi bi-person-x-fill"></i> Unassign
          </div>
        </div>
      )}
    </div>
  );
};

const ModernTaskDetailContent = ({ task, timeline, timelineStatus }) => {
  if (!task) return null;

  // Helper for grid items
  const GridItem = ({ icon, label, value }) => (
    <div className="d-flex align-items-start mb-3">
      <div className="me-3 mt-1 text-primary opacity-75">
        <i className={`bi ${icon}`} style={{ fontSize: "1.1rem" }}></i>
      </div>
      <div>
        <div
          className="text-muted small text-uppercase fw-bold"
          style={{ fontSize: "0.7rem", letterSpacing: "0.5px" }}
        >
          {label}
        </div>
        <div
          className="fw-medium text-dark"
          style={{ wordBreak: "break-word", fontSize: "0.9rem" }}
        >
          {value || "-"}
        </div>
      </div>
    </div>
  );

  return (
    <div className="p-4" style={{ backgroundColor: "#fdfdfd" }}>
      {/* 1. HERO HEADER */}
      <div className="d-flex justify-content-between align-items-start mb-4 pb-3 border-bottom">
        <div>
          <div className="d-flex align-items-center gap-2 mb-2">
            <span className="badge bg-light text-secondary border">
              Query #{task.QueryNo}
            </span>
            <span className="text-muted small">
              <i className="bi bi-calendar3 me-1"></i> {task.FormattedDate}
            </span>
          </div>
          <h5 className="fw-bold text-dark mb-1" style={{ lineHeight: "1.4" }}>
            {task.TaskName || "Untitled Task"}
          </h5>
        </div>
        <div className="d-flex flex-column align-items-end gap-2">
          <span
            className={`badge px-3 py-2 rounded-pill ${task.QueryType === "Bug"
              ? "bg-danger-subtle text-danger"
              : "bg-info-subtle text-info"
              }`}
          >
            {task.QueryType || "New Task"}
          </span>
          <span
            className={`badge px-3 py-2 rounded-pill ${task.Priority === "High"
              ? "bg-warning-subtle text-warning-emphasis"
              : "bg-light text-dark border"
              }`}
          >
            {task.Priority || "Normal"} Priority
          </span>
        </div>
      </div>

      {/* 2. NATURE OF QUERY (Highlight Box) */}
      <div className="bg-white p-3 rounded-3 border mb-4 shadow-sm">
        <h6 className="text-primary fw-bold small text-uppercase mb-2">
          <i className="bi bi-chat-quote-fill me-2"></i>Nature of Query /
          Description
        </h6>
        <p
          className="mb-0 text-secondary"
          style={{ fontSize: "0.95rem", lineHeight: "1.6" }}
        >
          {task.NatureofQuery || "No description provided."}
        </p>
      </div>

      <div className="row g-4">
        {/* 3. CLIENT CARD */}
        <div className="col-md-6">
          <div
            className="card h-100 border-0 shadow-sm"
            style={{ backgroundColor: "#f8f9fa" }}
          >
            <div className="card-body">
              <h6 className="card-title fw-bold text-dark mb-3 border-bottom pb-2">
                <i className="bi bi-building-fill me-2 text-secondary"></i>{" "}
                Client Details
              </h6>

              <GridItem
                icon="bi-buildings"
                label="Company"
                value={task.CompanyName}
              />
              <GridItem
                icon="bi-shop"
                label="Client Name"
                value={task.ClientName}
              />
              <GridItem
                icon="bi-person-circle"
                label="User Name"
                value={task.Client_UserName}
              />
              <GridItem icon="bi-phone" label="Contact" value={task.MobileNo} />
            </div>
          </div>
        </div>

        {/* 4. SYSTEM INFO CARD */}
        <div className="col-md-6">
          <div
            className="card h-100 border-0 shadow-sm"
            style={{ backgroundColor: "#f8f9fa" }}
          >
            <div className="card-body">
              <h6 className="card-title fw-bold text-dark mb-3 border-bottom pb-2">
                <i className="bi bi-laptop-fill me-2 text-secondary"></i> System
                Info
              </h6>

              <GridItem
                icon="bi-box-seam"
                label="Project"
                value={task.ProjectName}
              />
              <GridItem
                icon="bi-window-sidebar"
                label="Form Name"
                value={task.FormName}
              />
              <GridItem
                icon="bi-menu-button-wide"
                label="Main Menu"
                value={task.MainMenuName || task.FormName}
              />
              <GridItem
                icon="bi-list-nested"
                label="Sub Menu"
                value={task.SubMenuName}
              />
              <GridItem
                icon="bi-calendar-check"
                label="Target Date"
                value={task.FormattedTargetDate}
              />
            </div>
          </div>
        </div>
      </div>

      {/* 5. TRACKING
          Hand-overs and status changes on one line, the same view the
          Development screen shows. This is the screen where a task is given
          out, and "where has this been already" is the question that decides
          who to give it to — a task that has come back twice from Testing
          wants a different answer to one nobody has touched. */}

      {/* The screenshots, read-only.
       *
       * This dialog is where somebody decides who a task goes to, and the
       * picture is often the only thing that says what the job actually is —
       * "the report is wrong" plus a screenshot of the wrong report. Leaving
       * it out meant opening the task form to look, on a screen that exists
       * so you do not have to.
       *
       * readOnly: assigning is not editing. Nothing here writes. */}
      {task.QueryCode && (
        <div className="mt-4">
          <TaskImages queryCode={task.QueryCode} readOnly />
        </div>
      )}

      <div className="mt-4">
        <div
          className="card border-0 shadow-sm"
          style={{ backgroundColor: "#f8f9fa" }}
        >
          <div className="card-body">
            <h6 className="card-title fw-bold text-dark mb-3 border-bottom pb-2 d-flex align-items-center justify-content-between">
              <span>
                <i className="bi bi-clock-history me-2 text-secondary"></i>
                Tracking
              </span>
              {task.TeamName && (
                <span className="text-muted fw-normal" style={{ fontSize: "0.75rem" }}>
                  currently with {task.TeamName}
                  {task.AssignedTo ? ` · ${task.AssignedTo}` : ""}
                </span>
              )}
            </h6>
            <TaskTimeline events={timeline} status={timelineStatus} />
          </div>
        </div>
      </div>
    </div>
  );
};

// ==========================================
// 3. MAIN COMPONENT
// ==========================================

const TaskAssignment = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  // VIEWER gets read-only access: they can open the page and see the data, but
  // every editable control is locked and no update can be dispatched.
  const currentRole = getCurrentRole();
  const isViewer = currentRole === "VIEWER";

  const [activeTab, setActiveTab] = useState("manual");
  const [isLoading, setIsLoading] = useState(false);
  const [filterPriority, setFilterPriority] = useState("unassigned");
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [selectedTask, setSelectedTask] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  const [showSolutionModal, setShowSolutionModal] = useState(false);
  const [solutionText, setSolutionText] = useState("");
  const [solutionError, setSolutionError] = useState("");
  const [pendingAssignment, setPendingAssignment] = useState(null);

  // --- NEW STATE for Filter Badge ---
  const [filteredRecordCount, setFilteredRecordCount] = useState(0);
  const [isFilterActive, setIsFilterActive] = useState(false);

  const lastRequestRef = useRef(null);
  const usersRequestedRef = useRef(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const tasksData = useSelector(selectGetTaskAssignment);
  const listStatus = useSelector(selectGetTaskAssignmentStatus);
  const usersData = useSelector(selectUsers);

  const teamList = useSelector(selectTeams);
  const membersByTeam = useSelector((state) => state.team.membersByTeam);

  /* Everything that has happened to the task whose details are open. Asked
     for only when the dialog opens rather than alongside the list — it is one
     round trip per task looked at, against one per task on the page. */
  const timeline = useSelector(selectTaskTimeline);
  const timelineStatus = useSelector(selectTaskTimelineStatus);

  useEffect(() => {
    if (showDetailModal && selectedTask?.QueryCode) {
      dispatch(taskTimelineApis({ queryCode: selectedTask.QueryCode }));
    }
  }, [showDetailModal, selectedTask?.QueryCode, dispatch]);

  useEffect(() => {
    if (usersRequestedRef.current) return;
    usersRequestedRef.current = true;
    dispatch(usersApis({ currentPage: 1, pageSize: 1000 }));
    dispatch(teamListApis({}));
  }, [dispatch]);

  // const usersList = useMemo(() => usersData?.data?.data || [], [usersData]);
  // const usersList = useMemo(() => {
  //   return usersData?.data?.data || [];
  // }, [usersData]);
  const usersList = useMemo(() => {
    const data = usersData?.data;

    if (Array.isArray(data)) {
      return data;
    }

    if (Array.isArray(data?.data)) {
      return data.data;
    }

    if (Array.isArray(data?.Data)) {
      return data.Data;
    }

    if (Array.isArray(data?.users)) {
      return data.users;
    }

    if (Array.isArray(usersData?.data?.data?.data)) {
      return usersData.data.data.data;
    }

    return [];
  }, [usersData]);

  /* No active-user filter needed on this page: the assignee picker is fed by
     /team/members, and sp_TeamMember_GetAll already filters u.Status = 1. The
     full usersList is used only to resolve names on existing rows, which must
     keep working for people who have since been deactivated. */

  console.log("USERS API RESPONSE:", usersData);
  console.log("USERS LIST:", usersList);

  // The Not Possible tab is a review list of rejected tasks, so it ignores both
  // the manual/automatic split and the assigned/unassigned filter — it needs the
  // ones a developer rejected alongside the ones nobody picked up.
  const isNotPossibleTab = activeTab === "notPossible";

  const buildListPayload = useCallback(() => {
    if (isNotPossibleTab) {
      return { notPossible: 1, currentPage, pageSize };
    }
    return {
      isManuallyRaised: activeTab === "manual" ? 1 : 0,
      currentPage,
      pageSize,
      edit: filterPriority.toLowerCase() === "assigned" ? 1 : 0,
    };
  }, [isNotPossibleTab, activeTab, currentPage, pageSize, filterPriority]);

  /* The payload builder, in a ref, because the thing that calls it is stale.
   *
   * `columns` is memoized on [usersList, activeTab, isMobile, isViewer,
   * teamList, membersByTeam] — deliberately, so the grid is not rebuilt on
   * every keystroke. Its cell renderers close over handleUpdate, which closes
   * over refreshCurrentView, which closes over buildListPayload. None of
   * those are rebuilt when the FILTER or the PAGE changes, because none of
   * them are in that dependency list.
   *
   * So: land on Unassigned, switch the chip to Assigned. The chip is state,
   * so it updates; the effect above refetches with edit=1 and the assigned
   * rows appear. Then edit a row. The renderer still holds the handleUpdate
   * built at mount, whose buildListPayload still says edit=0 — and the
   * "refresh" after the save quietly replaced the assigned list with the
   * unassigned one, under a chip still reading Assigned. Exactly the same
   * happens to currentPage: save a row on page 3 and page 1 comes back while
   * the pager still says 3.
   *
   * Adding filterPriority and currentPage to the columns deps would fix
   * today's two symptoms and rebuild the entire grid config to do it — and
   * would break again the next time a piece of state joins the payload and
   * nobody remembers this list. A ref cannot go stale: the closure captures
   * the ref object, which never changes, and reads the current builder out of
   * it at the moment of the click. */
  const buildListPayloadRef = useRef(buildListPayload);
  useEffect(() => {
    buildListPayloadRef.current = buildListPayload;
  }, [buildListPayload]);

  useEffect(() => {
    const apiPayload = buildListPayload();
    const key = JSON.stringify(apiPayload);
    if (lastRequestRef.current === key) return;
    lastRequestRef.current = key;
    dispatch(getTaskAssignmentApis(apiPayload));
  }, [dispatch, buildListPayload]);


  /* The assignee dropdown must offer only the row's own team, so the people
     for every team present in the grid are fetched once the list arrives.
     Fetching per row would fire the same request dozens of times; fetching
     all users would put Web App names on a Database task. */
  useEffect(() => {
    const raw = tasksData?.data?.data || tasksData?.data || tasksData || [];
    if (!Array.isArray(raw)) return;

    const needed = [
      ...new Set(raw.map((r) => r.TeamCode).filter(Boolean)),
    ].filter((code) => !membersByTeam[code]);

    needed.forEach((teamCode) => dispatch(teamMembersApis({ teamCode })));
    // membersByTeam is intentionally not a dependency: it changes as each
    // fetch lands, and re-running on it would loop.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tasksData, dispatch]);

  const processedData = useMemo(() => {
    const raw = tasksData?.data?.data || tasksData?.data || tasksData || [];
    if (!Array.isArray(raw)) return [];
    return raw.map((item) => ({
      ...item,
      DisplayClient: item.ClientName || item.CompanyName || "-",
      AssignedTo:
        usersList.find((u) => u.UserCode === item.AssigneeCode)?.UserName ||
        null,
      EstimateTime: minutesToHHMM(item.EstimateTime),
      /* Two shapes of the same day: the ISO one is what the picker and the
         payload speak, the written one is what the filter searches. */
      TargetDateValue: toDateOnly(item.TargetDate),
      FormattedTargetDate: formatDateOnly(item.TargetDate),
      FormattedDate: item.QueryDate
        ? new Date(item.QueryDate).toLocaleDateString("en-GB", {
          day: "numeric",
          month: "short",
          year: "numeric",
        })
        : "-",
    }));
  }, [tasksData, usersList]);

  /* The chips read the whole list; the grid is handed the filtered rows.
     Status comes from either casing — this list has carried both. */
  const {
    rows: targetRows,
    counts: targetCounts,
    mode: targetMode,
    toggle: toggleTarget,
  } = useTargetDateFilter(processedData, {
    date: (r) => r.TargetDate,
    status: (r) => r.TaskStatus || r.Status,
  });

  /* Reload the list the user is actually looking at.
   *
   * Reads the builder out of the ref, never from this render's closure — see
   * buildListPayloadRef above. Called from cell renderers that can be several
   * filter changes older than the current state.
   *
   * lastRequestRef is cleared so the effect's de-duplication does not swallow
   * a genuine reload: the payload is unchanged after a save, and it is the
   * DATA behind it that moved. */
  const refreshCurrentView = () => {
    lastRequestRef.current = null;
    dispatch(getTaskAssignmentApis(buildListPayloadRef.current()));
  };

  /* Coming back to this tab re-asks for the list — the same refresh a save
     already performs, for the same reason: the rows have moved on. */
  useTabRefresh(refreshCurrentView);

  const processUpdateApi = async (row, changes, solutionVal = "") => {
    setIsLoading(true);

    try {
      const payload = {
        queryCode: row.QueryCode || row.QueryNo,
        priority: changes.priority || row.Priority || "Medium",
        assigneeCode:
          changes.userCode !== undefined ? changes.userCode : row.AssigneeCode,
        // Say it, rather than leaving the server to read it out of an empty
        // assigneeCode. A priority or duration edit sends no assignee either,
        // and the two used to be indistinguishable — so Unassign reported
        // success and left the person exactly where they were.
        //
        // Set for a team correction as well: handleUpdate clears the assignee
        // there on purpose, because the person who held the task is in the
        // team being corrected away from.
        ...(changes.userCode === null && { unassign: true }),
        estimateTime:
          changes.time !== undefined
            ? timeToMinutes(changes.time)
            : timeToMinutes(row.EstimateTime),
        queryType: changes.queryType || row.QueryType || "New Task",
        /* Re-sent unchanged by every other kind of edit, so the procedure is
           given back the date it already holds rather than a NULL it would
           have to interpret. */
        targetDate:
          changes.targetDate !== undefined
            ? changes.targetDate
            : row.TargetDateValue || null,
        // Fallbacks that check both casing variations
        isUpdate:
          changes.isUpdate !== undefined
            ? changes.isUpdate
            : row.IsUpdate || row.isUpdate || "None",
        solution: solutionVal,
        // Only sent when the team is actually being corrected. Sending the
        // current team on every edit would make each priority change look
        // like a hand-over in the task's history.
        ...(changes.teamCode !== undefined && {
          teamCode: Number(changes.teamCode),
          remarks: "Team corrected on the assignment screen.",
        }),
      };
      const res = await dispatch(tasksAssignApis(payload));
      if (res?.payload?.success) {
        toast.success("Task Updated!");

        /* Losing the assignee has to be said out loud.
         *
         * A team change clears it, and that is the point — but the only sign of
         * it on this screen would otherwise be an avatar quietly turning back
         * into an empty circle after the refresh, which reads as the save
         * having gone wrong rather than having done exactly what it should. */
        const handover = res.payload?.data?.data?.[0];
        const hadAssignee = Number(row.AssigneeCode) > 0;

        if (
          changes.teamCode !== undefined &&
          hadAssignee &&
          handover &&
          !handover.ToUserCode
        ) {
          toast.info(
            `${row.AssigneeName || "The assignee"} was removed — the task is now in the ${
              handover.ToTeamName || "new"
            } team's queue, waiting to be picked up.`,
          );
        }

        refreshCurrentView();
      }
    } catch (e) {
      console.error(e);
      toast.error("Update Failed");
    } finally {
      setIsLoading(false);
    }
  };

  const handleUpdate = async (row, changes) => {
    // Read-only guard: a viewer can never write, even if a control slips through.
    if (isViewer) return;

    // Sending a task to another team sends it to the TEAM. Whoever was on it
    // held it as work belonging to the old team; the new team's queue is where
    // it has to arrive, so the assignee goes.
    //
    // userCode: null is what says so — processUpdateApi turns an explicit null
    // into unassign: true, which is the only thing the procedure reads as "clear
    // the assignee" rather than "this edit does not mention one".
    if (changes.teamCode !== undefined) {
      await processUpdateApi(row, { ...changes, userCode: null });
      return;
    }

    if (changes.userCode !== undefined && changes.userCode !== null) {
      // This guard stops a task being pulled out from under a developer who has
      // already started it, so it only applies once somebody actually holds the
      // task. An unassigned row keeps whatever Status an earlier attempt left
      // behind ("Not Possible", "In Progress"), and blocking on that stale value
      // made those rows impossible to assign at all — they could never leave the
      // Unassigned queue.
      //
      // "Not Possible" is work a developer handed back, not work in flight, so
      // it stays re-assignable however it is held — that is the whole point of
      // the Not Possible tab, which is now the only place such a row appears.
      //
      // "Reassign" is the same kind of thing and was missing, which made the
      // guard bite the one case it exists to allow: a deployer sets a task to
      // Reassign precisely to say "this needs to go to somebody else", and the
      // task then could not be given to anybody — the only way out was to edit
      // the status somewhere else first. A task in flight is In Progress,
      // Testing or On Hold; Reassign is a task standing still, waiting to be
      // handed on.
      const REASSIGNABLE_STATUSES = ["Not Started", "Not Possible", "Reassign"];
      const hasAssignee = Number(row.AssigneeCode) > 0;
      const currentStatus = row.TaskStatus || row.Status || "Not Started";
      if (hasAssignee && !REASSIGNABLE_STATUSES.includes(currentStatus)) {
        toast.warning(
          `Cannot re-assign user. Task status is "${currentStatus}".`,
        );
        return;
      } // --- VALIDATION: Prevent assignment if IsUpdate is still "None" ---

      /* Everything the row still needs, asked ONCE.
       *
       * These three were three separate checks, each returning early with its
       * own toast. Assigning a fresh task therefore went: pick a person, get
       * told to set Is Update, set it, pick the person again, get told to set
       * Duration, set it, pick the person AGAIN, get told to set a Target
       * Date. Three refusals and three trips back to the picker before the
       * screen would even show the solution box — for a row that was missing
       * all three from the moment it arrived, which is the normal state of a
       * newly raised task.
       *
       * The rules are unchanged. Only the telling is: the row is checked as a
       * whole, and what is missing is said in one sentence, so it can be
       * filled in one pass.
       *
       * Order matters in the message — it is the order of the columns on the
       * screen, so the sentence reads left to right along the row somebody is
       * about to edit. */
      const missing = [];

      const currentIsUpdate = row.IsUpdate || row.isUpdate || "None";
      if (currentIsUpdate === "None" || currentIsUpdate === "") {
        missing.push("Is Update");
      }

      if (
        !row.EstimateTime ||
        row.EstimateTime === "00 : 00" ||
        row.EstimateTime === "00:00"
      ) {
        missing.push("Duration");
      }

      /* Optional when the task was raised, compulsory here. Handing somebody
         a task without saying when it is wanted is how a queue turns into a
         pile — and this is the moment the question can actually be answered,
         because the person assigning it knows both the work and who is free.

         A date entered on the Create screen is already in the box, so for
         those tasks this never fires. */
      if (!row.TargetDateValue) {
        missing.push("Target Date");
      }

      if (missing.length) {
        const list =
          missing.length === 1
            ? missing[0]
            : `${missing.slice(0, -1).join(", ")} and ${missing[missing.length - 1]}`;

        toast.warning(
          `Set ${list} on this row before assigning it.`,
          /* Longer than the default: three field names is a sentence to read
             and then act on, not a flash. */
          { autoClose: 6000 },
        );
        return;
      } // --- NEW LOGIC: Check for Reassignment ---
      // If AssigneeCode exists and isn't 0/empty, it's a reassignment

      const isReassignment = Boolean(
        row.AssigneeCode && row.AssigneeCode !== "0" && row.AssigneeCode !== 0,
      );

      if (isReassignment) {
        // Skip the modal entirely and just process the update
        await processUpdateApi(row, changes);
        return;
      } // First-time assignment: Show the modal

      setPendingAssignment({ row, changes });
      setSolutionText("");
      setSolutionError("");
      setShowSolutionModal(true);
      return;
    }
    await processUpdateApi(row, changes);
  };

  const handleSolutionSubmit = () => {
    const trimmedSolution = solutionText.trim();

    // 1. Check if empty
    if (!trimmedSolution) {
      setSolutionError("Solution description is required.");
      return;
    }

    // 2. Check if less than 10 characters
    if (trimmedSolution.length < 10) {
      setSolutionError("Solution must be at least 10 characters long.");
      return;
    }

    // 3. Proceed if valid
    if (pendingAssignment) {
      processUpdateApi(
        pendingAssignment.row,
        pendingAssignment.changes,
        trimmedSolution, // Send the trimmed text
      );
      setShowSolutionModal(false);
      setPendingAssignment(null);
      setSolutionText(""); // Reset
      setSolutionError("");
    }
  };

  // --- COLUMNS ---
  const columns = useMemo(
    () => [
      {
        label: "Client",
        key: "DisplayClient",
        width: "130px",
        cellClass: "text-cell-wrap",
        /* Both needed for the column filter to do anything: the grid matches
           and sorts on these, not on what render() paints. Every other text
           column here has them — this one did not, because until now it was
           the first column and the first column had no filter to feed. */
        searchValue: (r) => r.DisplayClient || "",
        sortValue: (r) => r.DisplayClient || "",
        render: (r) => <span className="fw-medium">{r.DisplayClient}</span>,
      },

      {
        label: "Form Name",
        key: "FormName",
        width: "180px",
        cellClass: "text-cell-wrap",
        searchValue: (r) => r.FormName || r.MainMenuName || "",
        sortValue: (r) => r.FormName || r.MainMenuName || "",
        render: (r) => (
          <div
            style={{
              wordBreak: "break-all",
              whiteSpace: "normal",
              overflow: "hidden",
            }}
          >
            {r.FormName || r.MainMenuName || "-"}
          </div>
        ),
      },
      {
        label: "Task Name",
        key: "TaskName",
        width: "250px",
        cellClass: "text-cell-wrap",
        // ---> ADD THESE TWO LINES <---
        searchValue: (r) => (r.TaskName || "") + " " + (r.NatureofQuery || ""),
        sortValue: (r) => r.TaskName || r.NatureofQuery || "",

        render: (r) => (
          <span
            onClick={() => {
              setSelectedTask(r);
              setShowDetailModal(true);
            }}
            className="text-primary fw-medium cursor-pointer"
            style={{ cursor: "pointer" }}
          >
            <span>{r.TaskName || r.NatureofQuery}</span>
          </span>
        ),
      },
      {
        label: "Query No",
        key: "QueryNo",
        width: "100px",
        render: (r) => (
          <span className="fw-bold text-secondary">#{r.QueryNo}</span>
        ),
      },

      {
        label: "Date",
        key: "FormattedDate",
        width: "110px",
        type: "date",
        render: (r) => r.FormattedDate,
      },
      {
        label: "Priority",
        key: "Priority",
        width: "130px",
        render: (r) => (
          <PrioritySelect
            initialValue={r.Priority}
            onChange={(v) => handleUpdate(r, { priority: v })}
            readOnly={isViewer}
          />
        ),
      },
      {
        label: "Query Type",
        key: "QueryType",
        width: "130px",
        render: (r) => (
          <QueryTypeSelect
            initialValue={r.QueryType}
            onChange={(v) => handleUpdate(r, { queryType: v })}
            readOnly={isViewer}
          />
        ),
      },
      {
        label: "Is Update",
        key: "IsUpdate",
        width: "140px",
        render: (r) => (
          <IsUpdateSelect
            initialValue={r.IsUpdate || r.isUpdate} // <-- Checks both casings
            onChange={(v) => handleUpdate(r, { isUpdate: v })}
            readOnly={isViewer}
            needed={
              !r.AssigneeCode &&
              ["None", "", null, undefined].includes(r.IsUpdate || r.isUpdate)
            }
          />
        ),
      },
      {
        label: "Duration",
        key: "EstimateTime",
        width: "120px",
        render: (r) => (
          <DurationEditor
            initialDuration={r.EstimateTime}
            onSave={(v) => handleUpdate(r, { time: v })}
            readOnly={isViewer}
            needed={
              !r.AssigneeCode &&
              (!r.EstimateTime ||
                r.EstimateTime === "00 : 00" ||
                r.EstimateTime === "00:00")
            }
          />
        ),
      },
      {
        label: "Target Date",
        key: "TargetDateValue",
        width: "150px",
        type: "date",
        searchValue: (r) => r.FormattedTargetDate || "",
        sortValue: (r) => r.TargetDateValue || "",
        render: (r) => (
          <TargetDateEditor
            value={r.TargetDateValue}
            onSave={(v) => handleUpdate(r, { targetDate: v })}
            readOnly={isViewer}
            required={!r.AssigneeCode}
          />
        ),
      },
      {
        label: "Team",
        key: "TeamName",
        width: "130px",
        type: "select",
        filterType: "select",
        searchValue: (r) => r.TeamName || "",
        render: (r) => (
          <TeamSelect
            initialValue={r.TeamCode}
            teams={teamList}
            onChange={(code) => handleUpdate(r, { teamCode: code })}
            readOnly={isViewer}
          />
        ),
      },
      {
        label: "Assign",
        key: "AssignedTo",
        width: "100px",
        render: (r) => (
          <UserSelect
            initialUser={r.AssignedTo}
            /* Only this task's own team. A Database task can never be handed
               to somebody in Web App, because they are not in the list — and
               the server refuses it regardless. Falls back to empty rather
               than to every user, so a team whose members have not loaded
               shows "No users found" instead of the wrong ones. */
            userOptions={membersByTeam[r.TeamCode] || []}
            onAssign={(u) => handleUpdate(r, { userCode: u?.UserCode || null })}
            readOnly={isViewer || !r.TeamCode}
          />
        ),
      },
    ],
    [usersList, activeTab, isMobile, isViewer, teamList, membersByTeam],
  );

  // Get the total records for the badge
  const totalRecords =
    tasksData?.data?.totalRecords ||
    tasksData?.data?.total ||
    processedData.length;

  return (
    <div className="report-container">
      {/* HEADER: RESPONSIVE LAYOUT FIX */}
      <PageHeader
        icon={<i className="bi bi-person-check"></i>}
        title="Task Assignment"
        subtitle={
          <div
            className="d-flex align-items-center flex-wrap gap-2"
            style={{
              marginTop: "2px",
            }}
          >
            <span
              style={{
                fontSize: "0.78rem",
                color: "#6c757d",
                fontWeight: "500",
              }}
            >
              Assign and manage tasks to team members
            </span>

            {/* RECORDS */}
            <StatChip
              icon="bi-clipboard-data-fill"
              tone="blue"
              label="Records"
              value={totalRecords}
            />

            {/* FILTERED */}
            <StatChip
              icon="bi-funnel-fill"
              tone="orange"
              label="Filtered"
              value={isFilterActive ? filteredRecordCount : 0}
            />

            {/* Late work, on the screen that decides who picks it up next. */}
            <TargetDateChips
              counts={targetCounts}
              mode={targetMode}
              onToggle={toggleTarget}
            />
          </div>
        }
        actions={
          // The Not Possible list spans both assigned and unassigned rows, so
          // the filter would only mislead there.
          isNotPossibleTab ? null : (
            <PriorityFilterDropdown
              value={filterPriority}
              onChange={setFilterPriority}
            />
          )
        }
      />

      {/* TABS */}
      <div className="tab-container">
        <div
          className={`tab-item ${activeTab === "manual" ? "active" : ""}`}
          onClick={() => {
            setActiveTab("manual");
            setCurrentPage(1);
          }}
        >
          Manually Raised
        </div>
        <div
          className={`tab-item ${activeTab === "automatic" ? "active" : ""}`}
          onClick={() => {
            setActiveTab("automatic");
            setCurrentPage(1);
          }}
        >
          Automatically Raised
        </div>
      </div>

      <ReportTable
        data={targetRows}
        columns={columns}
        // stickyFirstCol={true}
        onExport={() => alert("Export logic")}
        // Connect the table data/filters to our new state
        onDataChange={(data) => setFilteredRecordCount(data.length)}
        onFilterChange={(filters) => setIsFilterActive(filters.length > 0)}
        rowClassName={(r) => {
          const state = targetDateState(r.TargetDate, r.TaskStatus || r.Status);
          return state === "overdue"
            ? "is-target-over"
            : state === "due"
              ? "is-target-due"
              : "";
        }}
      />

      {/* NEW MODERN DETAILS MODAL */}
      <Modal
        show={showDetailModal}
        onClose={() => setShowDetailModal(false)}
        title="Task Details"
        maxWidth="800px"
      >
        {selectedTask && (
          <ModernTaskDetailContent
            task={selectedTask}
            timeline={timeline}
            timelineStatus={timelineStatus}
          />
        )}
      </Modal>

      {/* SOLUTION MODAL */}
      <Modal
        show={showSolutionModal}
        onClose={() => setShowSolutionModal(false)}
        title="Add Solution"
        maxWidth="500px"
      >
        <div className="p-3">
          <div className="mb-3">
            <label className="form-label fw-bold text-dark">
              Solution <span className="text-danger">*</span>
            </label>
            <textarea
              className={`form-control ${solutionError ? "is-invalid" : ""}`}
              rows="4"
              value={solutionText}
              onChange={(e) => {
                setSolutionText(e.target.value);
                // Clear error immediately if user types enough characters
                if (solutionError && e.target.value.trim().length >= 10) {
                  setSolutionError("");
                }
              }}
              placeholder="Enter solution details (min 10 chars)..."
            ></textarea>

            {/* Error Message Display */}
            {solutionError && (
              <div className="invalid-feedback d-block">{solutionError}</div>
            )}

            {/* Character Count Helper */}
            {!solutionError && (
              <div
                className="form-text text-end"
                style={{ fontSize: "0.8rem" }}
              >
                {solutionText.trim().length}/10 required characters
              </div>
            )}
          </div>

          <div className="d-flex justify-content-end gap-2">
            <button
              className="btn btn-light border"
              onClick={() => setShowSolutionModal(false)}
            >
              Cancel
            </button>
            <button className="btn btn-primary" onClick={handleSolutionSubmit}>
              Assign Task
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default TaskAssignment;
